/* eslint-disable import/no-extraneous-dependencies */
import { createApp } from 'vue';
import { createRouter, createWebHashHistory } from 'vue-router';

import { VueDraggableNext } from 'vue-draggable-next';

import LaunchDarkly from '@tagboard/launchdarkly';
import App from './components/App.vue';

import store from './store/producer';

import ColorPickerComponent from './components/colorpicker/ColorPicker.vue';

import DropdownComponent from './shared/dropdown/Dropdown.vue';
import InputTagComponent from './shared/input-tag/InputTag.vue';

import ProducerDashboard from './components/manage-productions/ProducerDashboard.vue';
import NotFound from './routes/404/404.vue';
import Production from './routes/production/Production.vue';

import SocketIO from './plugins/socket-io';
import Toasts from './plugins/toasts';

import clickOutside from "./directives/clickOutside";

import '../styles/tailwind.css';
import '../sass/producer.scss';

import './utils/filters'; // Referenced in child components
import instrument from './instrument';

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/',
      redirect: {
        name: 'productions',
        params: {
          tab: 'all',
        },
      },
    },
    {
      path: '/productions/:tab/:owner?',
      component: ProducerDashboard,
      name: 'productions',
      props: (route) => ({ tab: route.params.tab || 'all' }),
    },
    {
      path: '/404',
      component: NotFound,
      name: '404',
    },
    {
      path: '/:production_id',
      component: Production,
      name: 'production',
      children: [
        {
          // There used to be specific routes for each tab (e.g. /:production_id/graphics)
          // this will just catch all of those and redirect to the production route
          path: '*',
          redirect: {
            name: 'production',
          },
        },
      ],
    },
  ],
});

const app = createApp(App);

app.use(store);
app.use(router);

app.directive('click-outside', clickOutside);

// Expose this.$ld plugin
app.use(LaunchDarkly.vuePlugin({
  userApi: process.env.USER_API,
  clientId: process.env.LAUNCH_DARKLY_CLIENT_ID,
}));

app.component('input-tag', InputTagComponent);
app.component('colorpicker', ColorPickerComponent);
app.component('dropdown', DropdownComponent);
app.component('draggable', VueDraggableNext);

// Replicate old school VueSocketIO plugin by registering all event listeners found in store actions.
app.use(SocketIO, { store });

// Setup global toast methods
app.use(Toasts);

// shim for this.$set until we refactor all of those out, since Vue3 doesn't need it
app.config.globalProperties.$set = (obj, key, value) => {
  obj[key] = value; // eslint-disable-line no-param-reassign
};

instrument(app);

window.$app = app;
window.$store = store;

window.addEventListener('message', (e) => {
  if (typeof e.data === 'string' && e.data.indexOf('app:') === 0 && window.assetmanfn) {
    try {
      const payload = JSON.parse(e.data.replace('app:', ''));
      const asset = payload?.asset || payload;
      const meta = payload?.meta ?? asset?.meta ?? {};
      window.assetmanfn(asset, meta);
    } catch (err) {
      console.error('Failed to parse received message:', err);
    }
  }
}, false);

window.addEventListener('unhandledrejection', (e) => {
  console.log('Unhandled Rejection:', e.reason); // eslint-disable-line no-console
});

router.isReady().then(() => {
  app.mount('#app');
});
